import axios from "axios";
import { useCallback, useEffect, useState } from "react";
import {
  buildDocumentListUrl,
  DOCUMENTS_PAGE_SIZE,
  sortDocumentsByDynamicDate,
} from "../functions/documentList";
import useAxiosKeycloak from "./useAxiosKeycloak";

const usePaginatedDocuments = ({ keycloak, endpoint, baseQuery = "", errorMessages }) => {
  const axiosKeycloak = useAxiosKeycloak(keycloak);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [noDocuments, setNoDocuments] = useState(false);
  const [errorMessageConnection, setErrorMessageConnection] = useState("");
  const [page, setPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [activeFilterQuery, setActiveFilterQuery] = useState("");

  const applyDocuments = useCallback((responseData, targetPage, filterQuery) => {
    const sortedDocuments = sortDocumentsByDynamicDate(responseData);

    setDocuments(sortedDocuments);
    setPage(targetPage);
    setActiveFilterQuery(filterQuery);
    setHasNextPage(responseData.length === DOCUMENTS_PAGE_SIZE);
    setErrorMessageConnection("");

    if (!filterQuery && targetPage === 1) {
      setNoDocuments(responseData.length === 0);
    }
  }, []);

  const fetchDocuments = useCallback(async (targetPage = 1, filterQuery = "") => {
    setLoading(true);

    try {
      const url = buildDocumentListUrl({
        endpoint,
        baseQuery,
        filterQuery,
        page: targetPage,
        size: DOCUMENTS_PAGE_SIZE,
      });
      const response = await axiosKeycloak.get(url, { timeout: 20000 });
      const responseData = Array.isArray(response.data) ? response.data : [];

      if (targetPage > 1 && responseData.length === 0) {
        setHasNextPage(false);
        return;
      }

      applyDocuments(responseData, targetPage, filterQuery);
    } catch (error) {
      if (axios.isCancel(error)) {
        setErrorMessageConnection(errorMessages.rechazo);
      } else if (error.code === "ECONNABORTED") {
        setErrorMessageConnection(errorMessages.peticionTardo);
      } else {
        setErrorMessageConnection(
          error.response?.data?.errorMsg || errorMessages.networkError
        );
      }
    } finally {
      setLoading(false);
    }
  }, [applyDocuments, axiosKeycloak, baseQuery, endpoint, errorMessages]);

  useEffect(() => {
    fetchDocuments(1, "");
  }, [fetchDocuments]);

  const handleFilterResults = useCallback((responseData, filterQuery = "") => {
    const documentsResponse = Array.isArray(responseData) ? responseData : [];
    applyDocuments(documentsResponse, 1, filterQuery);
  }, [applyDocuments]);

  const handlePageChange = useCallback((targetPage) => {
    if (targetPage < 1 || loading) {
      return;
    }

    fetchDocuments(targetPage, activeFilterQuery);
  }, [activeFilterQuery, fetchDocuments, loading]);

  return {
    documents,
    loading,
    noDocuments,
    errorMessageConnection,
    page,
    hasNextPage,
    pageSize: DOCUMENTS_PAGE_SIZE,
    handleFilterResults,
    handlePageChange,
  };
};

export default usePaginatedDocuments;
